/**
 * Module dependencies.
 */

import { Image, Navigation, Subtitle, Type } from 'src/components/core';
import { ModelRangeProps } from 'src/types/model-ranges';
import { ReactElement, useMemo } from 'react';
import { TechnologyProps } from 'src/types/technologies';
import { media, units, useBreakpoint } from '@untile/react-components';
import { theme } from 'styled-tools';
import { useLocalizedRoute } from 'src/core/utils/routes';
import Container from 'src/components/core/layout/container';
import map from 'lodash/map';
import size from 'lodash/size';
import styled from 'styled-components';
import useTranslate from 'src/hooks/use-translate';

/**
 * `Props` type.
 */

type Props = Pick<
  ModelRangeProps | TechnologyProps,
  'gallery' | 'slug' | 'subtitle' | 'title'
> & {
  pageType: 'modelRange' | 'technology';
};

/**
 * `StyledContainer` styled component.
 */

const StyledContainer = styled(Container)`
  padding: 0;
`;

/**
 * `Grid` styled component.
 */

const Grid = styled.div`
  display: grid;
  grid-template-areas:
    '. navigation   .'
    '. titleWrapper .'
    '. gallery      .';
  grid-template-columns: ${theme('grid.gutterMobile')}px 1fr ${theme(
      'grid.gutterMobile'
    )}px;

  ${media.min('md')`
    grid-template-areas:
      '. navigation   navigation   .            .'
      '. titleWrapper titleWrapper titleWrapper .'
      '. .            gallery      .            .';
    grid-template-columns: ${units(10.5)} 1fr ${units(102.5)} 1fr ${units(
    10.5
  )};
  `}
`;

/**
 * `StyledNavigation` styled component.
 */

const StyledNavigation = styled(Navigation)`
  grid-area: navigation;

  ${media.max('md')`
    margin-bottom: ${units(3)};
  `}
`;

/**
 * `TitleWrapper` styled component.
 */

const TitleWrapper = styled.div`
  display: flex;
  flex-direction: column;
  grid-area: titleWrapper;
  margin-bottom: ${units(3)};

  ${media.min('md')`
    align-items: center;
    margin-bottom: ${units(6)};
  `}
`;

/**
 * `GalleryWrapper` styled component.
 */

const GalleryWrapper = styled.div`
  align-items: center;
  display: grid;
  grid-area: gallery;
  grid-auto-columns: 100%;
  grid-row-gap: ${units(4)};

  ${media.min('md')`
    grid-row-gap: ${units(6)};
  `}
`;

/**
 * `ImageWrapper` styled component.
 */

const ImageWrapper = styled.div`
  margin: 0 auto;
  overflow: hidden;
  position: relative;

  * {
    position: relative !important;
  }

  img {
    height: auto !important;
    max-height: initial !important;
    max-width: 100% !important;
    min-height: initial !important;
    min-width: initial !important;
    width: 670px !important;
  }

  ${media.max('md')`
    max-width: ${units(60)};
    width: 100%;
  `}
`;

/**
 * `StyledImage` styled component.
 */

const StyledImage = styled(Image)`
  cursor: pointer;
  transition: transform 0.3s ease;

  &:focus,
  &:hover {
    transform: scale(1.04);
  }
`;

/**
 * `GalleryPage` component.
 */

const GalleryPage = (props: Props): ReactElement => {
  const { gallery, pageType, slug, subtitle, title } = props;
  const routeResolver = useLocalizedRoute();
  const isMobile = useBreakpoint('md', 'max');
  const { translate } = useTranslate();
  const total = size(gallery);
  const normalizedTitle = `${title} - ${translate('common:labels.gallery')}`;
  const animProps = {
    'data-scroll': 'fade-in-up',
    'data-scroll-offset': '10%'
  };

  const goBackRoute = useMemo(() => {
    const route =
      pageType === 'modelRange' ? 'modelRangeDetails' : 'technologyDetails';

    return routeResolver(route, { slug });
  }, [pageType, routeResolver, slug]);

  return (
    <StyledContainer>
      <Grid>
        <StyledNavigation
          href={goBackRoute}
          label={translate('common:actions.goBack')}
        />

        <TitleWrapper>
          <Type.H1 {...animProps}>{normalizedTitle}</Type.H1>

          <Subtitle
            {...animProps}
            size={'large'}
          >
            {subtitle}
          </Subtitle>
        </TitleWrapper>

        {total > 0 && (
          <GalleryWrapper>
            {map(gallery, (imageUrl, index) => (
              <ImageWrapper
                {...(!isMobile && {
                  'data-scroll': true,
                  'data-scroll-speed': 1
                })}
                key={index}
              >
                <StyledImage
                  alt={imageUrl}
                  fill
                  src={imageUrl}
                  style={{ objectFit: 'cover' }}
                />
              </ImageWrapper>
            ))}
          </GalleryWrapper>
        )}
      </Grid>
    </StyledContainer>
  );
};

/**
 * Export `GalleryPage` component.
 */

export default GalleryPage;
